import { safeStorage } from "../camera/modes.js";

/** `world.render.quality` (modules/world settings): the user's choice; `auto` follows the frame rate. */
export type QualitySetting = "auto" | "low" | "high";
export type QualityTier = "low" | "high";

export const QUALITY_SETTINGS: readonly QualitySetting[] = ["auto", "low", "high"];
export const QUALITY_STORAGE_KEY = "uz.world.quality";

/** What a tier changes (docs/PHASE2_PLAN.md 2.13: phones get no window bands and DPR <= 1.5). */
export interface TierSettings {
  /** Upper bound of the device pixel ratio. */
  dprMax: number;
  /** Draw window bands on buildings. */
  windows: boolean;
  /** Labels farther than this (meters from the camera) are not drawn. */
  labelDistance: number;
  /** At most this many labels at once (nearest first). */
  maxLabels: number;
  /** Multi-sample anti-aliasing (fixed when the canvas is created). */
  antialias: boolean;
}

/**
 * The render pipeline level. `low` and `high` are the two user-visible tiers; `medium` is a step
 * the auto mode takes on the way down (it keeps ambient occlusion and anti-aliasing but drops
 * bloom and the third shadow cascade). It counts as `high` for everything that only knows two tiers.
 */
export type RenderLevel = "low" | "medium" | "high";

export interface LevelSettings {
  /** Post-processing pipeline (ambient occlusion, bloom, anti-aliasing, tone mapping, grade). */
  post: boolean;
  ao: boolean;
  bloom: boolean;
  /** Shadow cascades (1 = a single fitted shadow map). */
  cascades: number;
  /** Largest edge of the PBR texture layers, in pixels. */
  texSize: number;
}

export const LEVELS: Readonly<Record<RenderLevel, LevelSettings>> = {
  low: { post: false, ao: false, bloom: false, cascades: 1, texSize: 512 },
  medium: { post: true, ao: true, bloom: false, cascades: 2, texSize: 1024 },
  high: { post: true, ao: true, bloom: true, cascades: 3, texSize: 1024 },
};

/** `?fx=low|medium|high` forces a level (screenshots, the benchmark). */
export function fxOverride(search: string = globalThis.location?.search ?? ""): RenderLevel | null {
  const m = /[?&]fx=(low|medium|high)(?:&|$)/.exec(search);
  return m ? (m[1] as RenderLevel) : null;
}

/** The level in effect: a fixed choice, or the adaptive one in auto mode. */
export function effectiveLevel(setting: QualitySetting, auto: RenderLevel): RenderLevel {
  return fxOverride() ?? (setting === "auto" ? auto : setting);
}

export const TIERS: Readonly<Record<QualityTier, TierSettings>> = {
  low: { dprMax: 1, windows: false, labelDistance: 160, maxLabels: 24, antialias: false },
  high: { dprMax: 2, windows: true, labelDistance: 320, maxLabels: 60, antialias: true },
};

/** Phones and tablets start low in auto mode: coarse pointer, no hover. */
export function isCoarseDevice(): boolean {
  try {
    return typeof matchMedia === "function" && matchMedia("(pointer: coarse)").matches;
  } catch {
    return false;
  }
}

/** The tier to render with: a fixed choice, or the adaptive one in auto mode. */
export function effectiveTier(setting: QualitySetting, auto: RenderLevel): QualityTier {
  return effectiveLevel(setting, auto) === "low" ? "low" : "high";
}

export function initialAutoTier(coarse: boolean = isCoarseDevice()): RenderLevel {
  return coarse ? "low" : "high";
}

/** Device pixel ratio for a tier: capped by the tier and by the phone ceiling of 1.5. */
export function dprFor(tier: QualityTier, deviceRatio: number, coarse: boolean): number {
  const cap = Math.min(TIERS[tier].dprMax, coarse ? 1.5 : 2);
  return Math.max(0.75, Math.min(cap, deviceRatio));
}

export const isQualitySetting = (v: unknown): v is QualitySetting =>
  v === "auto" || v === "low" || v === "high";

export function loadQuality(
  storage: Pick<Storage, "getItem"> | null = safeStorage(),
): QualitySetting {
  try {
    const v = storage?.getItem(QUALITY_STORAGE_KEY);
    if (isQualitySetting(v)) return v;
  } catch {
    // Blocked storage: the default.
  }
  return "auto";
}

/** The remembered quality only when one was stored (`loadQuality` falls back to auto). */
export function storedQuality(
  storage: Pick<Storage, "getItem"> | null = safeStorage(),
): QualitySetting | undefined {
  try {
    const v = storage?.getItem(QUALITY_STORAGE_KEY);
    return isQualitySetting(v) ? v : undefined;
  } catch {
    return undefined;
  }
}

export function saveQuality(
  value: QualitySetting,
  storage: Pick<Storage, "setItem"> | null = safeStorage(),
): void {
  try {
    storage?.setItem(QUALITY_STORAGE_KEY, value);
  } catch {
    // Not persisted without storage.
  }
}
